import {
  AvailabilityAutobookLinkDataApiResponse,
  RoosterAutobookInterviewStatusResponse,
  RoosterAutobookMetaResponse
} from "./types/autobookAvailability.type";
import { RoosterLinkDataApiResponse } from "./types/roosterlinks.type";

import { RoosterLinkAvailabilityIDResponse } from "./types/roostermeeting.type";
import { baseUrlFunctions, baseUrlServer } from "./common";
import { axiosWithRetry } from "./utils/axiosRetry";
import axios from "axios";

export const getRoosterLinkData = async (requestPayload: {
  userId: string;
  linkId: string;
}) => {
  const url = `${baseUrlServer}/roosterlinks/${requestPayload.userId}/${requestPayload.linkId}`;
  try {
    const response = await axiosWithRetry(url, "get", { timeout: 120000 });
    const json = (await response.data) as RoosterLinkDataApiResponse;
    return json;
  } catch (error: unknown) {
    if (error instanceof Error) {
      console.error(error?.message);
    }
  }
};

export const getRoosterLinkMeetingData = async (requestPayload: {
  userId: string;
  internalUserId: string;
  roosterLinkId: string;
  linkId: string;
  availabilityId: string;
}) => {
  const url = `
${baseUrlFunctions}/personalMeetingLinks/virtual/roosterlinks/availability?personIdentifier=${requestPayload.userId}&profileIdentifier=${requestPayload.linkId}&availabilityId=${requestPayload.availabilityId}&userId=${requestPayload.internalUserId}&roosterLinkId=${requestPayload.roosterLinkId}`;
  try {
    const response = await axiosWithRetry(url, "get", { timeout: 120000 });
    const json = (await response.data) as RoosterLinkAvailabilityIDResponse;
    return json;
  } catch (error: unknown) {
    if (error instanceof Error) {
      console.error(error?.message);
    }
  }
};

export const validateRoosterLinkService = async ({
  personIdentifier,
  profileIdentifier,
  userUid
}: {
  personIdentifier: string;
  profileIdentifier: string;
  userUid: string;
}) => {
  const url = `${baseUrlFunctions}/personalMeetingLinks/roosterlinks/validate`;
  try {
    const response = await axiosWithRetry(url, "post", {
      data: { personIdentifier, profileIdentifier, userUid },
      timeout: 120000
    });
    const json = response.data as {
      code: string;
      error: false;
      invitedBy?: string;
    };
    return json;
  } catch (error: unknown) {
    if (error instanceof Error) {
      console.error(error?.message);
    }

    if (axios.isAxiosError(error) && error.response?.data) {
      return {
        error: true,
        code: error?.response?.data?.code,
        invitedBy: error?.response?.data?.invitedBy
      };
    }
  }
};

/**
 * Verifies an internalRef token and resolves to the tenant it was minted for,
 * or null when the token is absent/expired/forged. The tenant matters: the
 * caller must bind the T&C bypass to the link being viewed, otherwise a token
 * from any tenant would suppress any other tenant's terms (R495-405).
 */
export async function verifyInternalRefToken(
  token: string
): Promise<string | null> {
  try {
    const url = `${baseUrlServer}/roosterlinks/verify-internal-ref`;
    const response = await axios.post(url, { token }, { timeout: 10000 });
    if (response.data?.valid !== true) return null;
    // Only a string tenant can be bound to a link; guard the type so an
    // unexpected shape never reaches the store or violates the return contract.
    const client = response.data?.client;
    return typeof client === "string" && client ? client : null;
  } catch {
    return null;
  }
}

export async function getRoosterAutobookAvailabilityData(requestPayload: {
  linkId: string;
}) {
  const url = `${baseUrlServer}/availability/candidate/${requestPayload.linkId}`;
  try {
    const response = await axiosWithRetry(url, "get", { timeout: 120000 });
    return response.data as AvailabilityAutobookLinkDataApiResponse;
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 400) {
      return error.response.data as {
        error: string;
        initiatorName?: string;
        initiatorEmail?: string;
      };
    }
    console.log(error instanceof Error ? error?.message : error);
  }
  return;
}

export async function getRoosterAutobookAvailabilityMeta(requestPayload: {
  linkId: string;
}) {
  const url = `${baseUrlFunctions}/availability/calendar/candidateAvailability/${requestPayload.linkId}`;
  try {
    const response = await axiosWithRetry(url, "get", { timeout: 120000 });

    return response.data as RoosterAutobookMetaResponse;
  } catch (error: unknown) {
    console.log(error instanceof Error ? error?.message : error);
  }
}

export async function getRoosterAutobookInterviewStatus(requestPayload: {
  linkId: string;
}) {
  const url = `${baseUrlFunctions}/autobookRequest/autobookInterview/status`;
  try {
    const response = await axiosWithRetry(url, "post", {
      data: {
        availabilityId: requestPayload.linkId
      },
      timeout: 120000
    });

    return response.data as RoosterAutobookInterviewStatusResponse;
  } catch (error: unknown) {
    console.log(error instanceof Error ? error?.message : error);
  }
}
